import { blockAppearance } from '../textures/blockAppearance';
import type { PaletteEntry, StructureModel } from './types';

/**
 * こなや糸が「どちらへ伸びているか」を、まわりのブロックから決める。
 *
 * Java版の`.nbt`は接続方向をブロック自身が持っているが、統合版の`.mcstructure`は持たない。
 * 持たないまま描くと向きが分からず十字になってしまうので、Minecraftと同じように
 * 隣を見て判断する。結果は`north`/`south`/`west`/`east`としてブロックに書き戻す。
 */

const DIRECTIONS = [
  { key: 'north', dx: 0, dz: -1, opposite: 'south' },
  { key: 'south', dx: 0, dz: 1, opposite: 'north' },
  { key: 'west', dx: -1, dz: 0, opposite: 'east' },
  { key: 'east', dx: 1, dz: 0, opposite: 'west' },
] as const;

/** こなが信号をやり取りする相手。ここに無いブロックへは線を向けない。 */
const REDSTONE_PARTS = new Set([
  'redstone_wire', 'redstone_torch', 'redstone_wall_torch', 'redstone_block', 'redstone_lamp',
  'repeater', 'comparator', 'lever', 'dispenser', 'dropper', 'piston', 'sticky_piston',
  'piston_head', 'sticky_piston_head', 'observer', 'note_block', 'tnt', 'hopper', 'target',
  'daylight_detector', 'redstone_ore', 'deepslate_redstone_ore', 'rail', 'powered_rail',
  'activator_rail', 'detector_rail', 'command_block', 'chain_command_block',
  'repeating_command_block', 'bell', 'lightning_rod', 'sculk_sensor', 'crafter', 'dropper',
]);

const REDSTONE_SUFFIXES = ['_button', '_pressure_plate', '_door', '_trapdoor', '_fence_gate'];

function shortName(name: string): string {
  const separator = name.indexOf(':');
  return separator >= 0 ? name.slice(separator + 1) : name;
}

function isRedstonePart(name: string): boolean {
  const short = shortName(name);
  return REDSTONE_PARTS.has(short) || REDSTONE_SUFFIXES.some((suffix) => short.endsWith(suffix));
}

/** 接続が1本だけのときは、Minecraftと同じくその軸の反対側へも線を伸ばして直線にする。 */
function straightenSingle(links: Record<string, boolean>): void {
  const linked = DIRECTIONS.filter((direction) => links[direction.key]);
  if (linked.length !== 1) return;
  links[linked[0].opposite] = true;
}

export function resolveConnections(model: StructureModel): StructureModel {
  const wanted = model.palette.some((entry) => {
    const short = shortName(entry.name);
    return short === 'redstone_wire' || short === 'tripwire';
  });
  if (!wanted) return model;

  const [sizeX, sizeY, sizeZ] = model.size;
  const cell = (x: number, y: number, z: number) => (y * sizeZ + z) * sizeX + x;
  const grid = new Int32Array(sizeX * sizeY * sizeZ).fill(-1);
  for (const block of model.blocks) {
    grid[cell(block.position[0], block.position[1], block.position[2])] = block.state;
  }

  const nameAt = (x: number, y: number, z: number): string => {
    if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) return '';
    const state = grid[cell(x, y, z)];
    return state < 0 ? '' : shortName(model.palette[state].name);
  };

  const palette: PaletteEntry[] = [...model.palette];
  const cache = new Map<string, number>();

  /** 接続を書き足した見た目違いを、パレットの別エントリとして持たせる。 */
  const stateWith = (base: number, links: Record<string, boolean>): number => {
    const suffix = DIRECTIONS.map((direction) => (links[direction.key] ? '1' : '0')).join('');
    const key = `${base}:${suffix}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;

    const source = model.palette[base];
    const properties = { ...source.properties };
    for (const direction of DIRECTIONS) {
      properties[direction.key] = links[direction.key] ? 'side' : 'none';
    }
    palette.push({ ...source, properties });
    const state = palette.length - 1;
    cache.set(key, state);
    return state;
  };

  const blocks = model.blocks.map((block) => {
    const short = shortName(model.palette[block.state].name);
    if (short !== 'redstone_wire' && short !== 'tripwire') return block;

    const [x, y, z] = block.position;
    const links: Record<string, boolean> = { north: false, south: false, west: false, east: false };

    for (const direction of DIRECTIONS) {
      const nx = x + direction.dx;
      const nz = z + direction.dz;
      if (short === 'tripwire') {
        const neighbour = nameAt(nx, y, nz);
        links[direction.key] = neighbour === 'tripwire' || neighbour === 'tripwire_hook';
        continue;
      }
      // 同じ高さの相手か、1段上下のこな（階段状の配線）に繋がる。
      links[direction.key] = isRedstonePart(nameAt(nx, y, nz))
        || nameAt(nx, y - 1, nz) === 'redstone_wire'
        || nameAt(nx, y + 1, nz) === 'redstone_wire';
    }

    straightenSingle(links);
    return { ...block, state: stateWith(block.state, links) };
  });

  return { ...model, palette, blocks };
}

/** Bedrock omits pane/fence connections; keep explicit Java connections intact. */
export function resolveShapeConnections(model: StructureModel): StructureModel {
  const family = (name: string) => /_pane$|iron_bars$/.test(name) ? 'pane' : /_fence$/.test(name) ? 'fence' : /_wall$/.test(name) ? 'wall' : '';
  if (!model.palette.some(p => family(p.name))) return model;
  const cells = new Map(model.blocks.map(b => [b.position.join(','), b.state]));
  const palette = [...model.palette], variants = new Map<string, number>();
  const blocks = model.blocks.map(block => {
    const entry = palette[block.state], kind = family(entry.name);
    if (!kind || DIRECTIONS.every(d => entry.properties[d.key] !== undefined)) return block;
    const properties = {...entry.properties};
    for (const d of DIRECTIONS) {
      if (properties[d.key] !== undefined) continue;
      const state = cells.get([block.position[0]+d.dx, block.position[1], block.position[2]+d.dz].join(','));
      const neighbor = state === undefined ? null : palette[state];
      const connected = neighbor && (family(neighbor.name) === kind || /_fence_gate$/.test(neighbor.name) || blockAppearance(neighbor.name, neighbor.properties).occludes || (kind === 'pane' && /glass$/.test(neighbor.name)));
      properties[d.key] = kind === 'wall' ? connected ? 'low' : 'none' : connected ? 'true' : 'false';
    }
    if (kind === 'wall' && properties.up === undefined) properties.up = 'true';
    const key=block.state+JSON.stringify(properties);
    let state=variants.get(key);
    if (state === undefined) {state=palette.length;palette.push({...entry,properties});variants.set(key,state);}
    return {...block,state};
  });
  return {...model,palette,blocks};
}
